<script setup>
/* eslint-disable */
import store from '../store';
import {computed, ref, watch} from "vue";
import ClickHistory from "@/components/charts/ClickHistory.vue";
import ClicksByCountry from "@/components/charts/ClicksByCountry.vue";
import DashboardContainer from "@/components/Controls/charts/DashboardContainer.vue";
import ClicksByCollection from "@/components/charts/ClicksByCollection.vue";
import TopArticles from "@/components/charts/TopArticles.vue";
import TotalClicks from "@/components/charts/TotalClicks.vue";
import TopDomains from "@/components/charts/TopDomains.vue";
import ByUrlParam from "@/components/charts/ByUrlParam.vue";
import ArticleSummary from "@/components/charts/ArticleSummary.vue";
import ReportFilters from "@/components/Reporting/ReportFilters.vue";
import ReportHeader from "@/components/Reporting/ReportHeader.vue";
import ClickByUserAgent from "@/components/charts/ClickByUserAgent.vue";
import ClicksByReferrer from "@/components/charts/ClicksByReferrer.vue";
import {useReportDownloadUrls} from "@/composables/useReportDownloadUrl";
import TopArticlesCards from "@/components/charts/TopArticlesCards.vue";

const props = defineProps({
    collectionId: {type: String, required: false, default: ''},
    qpKey: {type: String, required: false, default: ''},
    qpValue: {type: String, required: false, default: ''},
    timespan: {type: String, required: false, default: 'last_month'},
    start: {type: String, required: false, default: null},
    groupBy: {type: String, required: false, default: 'week'},
    end: {type: String, required: false, default: null},
    age: {type: Number, required: false, default: 90},
    referrers: {type: String, required: false, default: ''},
    referrerMode: {type: String, required: false, default: ''},
    userAgents: {type: String, required: false, default: ''},
    userAgentMode: {type: String, required: false, default: ''},
});

const widgetReloadKeys = ref({});

console.log('query props', props)
const features = computed(() => store.getters.accountFeatures);

const colors = ['purple', 'violet', 'blue', 'cyan', 'green', 'yellow', 'orange', 'red'];


const filters = ref(null)

const {downloadUrl} = useReportDownloadUrls(store, filters, {limit: 1000});

function handleReloadWidget(widgetName) {
    widgetReloadKeys.value[widgetName] = Date.now();
}

</script>

<template>
    <div class="dashboard">
        <ReportHeader class="mt-2 ms-4 me-4 mb-2" />
        <ReportFilters @filters-changed="x => filters = x" v-bind="props"/>
        <div class="m-4">
            <div class="dashboard__body row">
                <dashboard-container
                    :span="1"
                    label="Total Account Clicks"
                    @reloadWidget="handleReloadWidget"
                    :widget-name="'TotalClicks'"
                >
                    <TotalClicks :filters="filters" :reload-trigger="widgetReloadKeys.TotalClicks"/>
                </dashboard-container>
                <dashboard-container
                    :span="1"
                    label="Articles"
                    @reloadWidget="handleReloadWidget"
                    :widget-name="'ArticleSummary'"
                >
                    <ArticleSummary :filters="filters" :reload-trigger="widgetReloadKeys.ArticleSummary"/>
                </dashboard-container>
                <dashboard-container
                    :span="2"
                    label="Clicks per Collection"
                    :download-url="downloadUrl('by_collection')"
                    download-name="by-collection.csv"
                    @reloadWidget="handleReloadWidget"
                    :widget-name="'ClicksByCollection'"
                >
                    <ClicksByCollection :filters="filters" :reload-trigger="widgetReloadKeys.ClicksByCollection"/>
                </dashboard-container>
                <dashboard-container
                    :span="4"
                    label="Click History"
                    :expand="true"
                    :download-url="downloadUrl(`by_collection_by_${filters && filters.groupBy}`)"
                    download-name="click-history.csv"
                    @reloadWidget="handleReloadWidget"
                    :widget-name="'ClickHistory'"
                >
                    <ClickHistory
                        :filters="filters"
                        class="bar-chart"
                        :by="filters && filters.groupBy || props.groupBy"
                        :reload-trigger="widgetReloadKeys.ClickHistory"
                    />
                </dashboard-container>
                <dashboard-container
                    :span="2"
                    label="Clicks per Country"
                    :download-url="downloadUrl('by_country')"
                    download-name="by-country.csv"
                    @reloadWidget="handleReloadWidget"
                    :widget-name="'ClicksByCountry'"
                >
                    <ClicksByCountry
                        :filters="filters"
                        class="pie-chart"
                        :reload-trigger="widgetReloadKeys.ClicksByCountry"
                    />
                </dashboard-container>
                <dashboard-container
                    :span="2"
                    label="Clicks per Referrer"
                    :download-url="downloadUrl('by_referrer')"
                    download-name="by-referrer.csv"
                    @reloadWidget="handleReloadWidget"
                    :widget-name="'ClicksByReferrer'"
                >
                    <ClicksByReferrer
                        :filters="filters"
                        class="bar-chart"
                        :by="groupBy"
                        :reload-trigger="widgetReloadKeys.ClicksByReferrer"
                    />
                </dashboard-container>
                <dashboard-container
                    :span="4"
                    label="Top Articles"
                    expand
                    :download-url="downloadUrl('top_articles')"
                    download-name="top-articles.csv"
                    @reloadWidget="handleReloadWidget"
                    :widget-name="'TopArticles'"
                >
                    <TopArticles :filters="filters" :reload-trigger="widgetReloadKeys.TopArticles"/>
                </dashboard-container>
                <dashboard-container
                    :span="4"
                    label="Top Domains"
                    expand
                    :download-url="downloadUrl('top_domains')"
                    download-name="top-domains.csv"
                    @reloadWidget="handleReloadWidget"
                    :widget-name="'TopDomains'"
                >
                    <TopDomains :filters="filters" :reload-trigger="widgetReloadKeys.TopDomains"/>
                </dashboard-container>
                <dashboard-container
                    :span="4"
                    label="URL Parameters"
                    expand
                    :download-url="downloadUrl('by_url_params')"
                    download-name="by-url-params.csv"
                    @reloadWidget="handleReloadWidget"
                    :widget-name="'ByUrlParam'"
                >
                    <ByUrlParam :filters="filters" :reload-trigger="widgetReloadKeys.ByUrlParam"/>
                </dashboard-container>
                <dashboard-container
                    :span="4"
                    label="By User Agent"
                    expand
                    :download-url="downloadUrl('by_user_agent')"
                    download-name="by-user-agent.csv"
                    @reloadWidget="handleReloadWidget"
                    :widget-name="'ClickByUserAgent'"
                >
                    <ClickByUserAgent
                        :filters="filters"
                        :reload-trigger="widgetReloadKeys.ClickByUserAgent"
                    />
                </dashboard-container>
<!--                <dashboard-container :span="1" label="Medium Numbers">-->
<!--                    <big-numbers :values="[{value: 42, unit: 'flops', label: 'Happiness'}, {value: 1337, unit: 'codes', label: 'Leetness'}]"></big-numbers>-->
<!--                </dashboard-container>-->
<!--                <dashboard-container :span="1" label="Small Numbers">-->
<!--                    <numbers :values="[{value: 42, unit: 'flops', label: 'Happiness'}, {value: 1337, unit: 'codes', label: 'Leetness'}, {value: 1337, unit: 'codes', label: 'Leetness'}, {value: 1337, unit: 'codes', label: 'Leetness'}]"></numbers>-->
<!--                </dashboard-container>-->

            </div>
        </div>
    </div>
</template>
<style scoped>
</style>

